<script lang="ts">
import ProductCheckoutModal from '@/components/modals/ProductCheckoutModal.vue'
import { useCheckoutBasket } from '@/plugins/useCheckoutBasket'
import type { Nullable, Plan } from '@/types'
import { toPeriod } from '@/utils/date-time-utils'
import {
  MembershipPeriods,
  NamedPlanTiers,
  PlanTiers,
  type TIcon,
  type TMembershipPeriodValue,
  Themes,
} from '@/utils/helper-objects'
import { type PropType, defineComponent } from 'vue'

/**
 * A card to display a plan.
 *
 */
export default defineComponent({
  components: {
    ProductCheckoutModal,
  },
  props: {
    /**
     * A configured plan object.
     */
    plan: {
      type: Object as PropType<Plan>,
      required: true,
    },
    /**
     * Updates the displayed features/pricing based on membership period.
     */
    selectedMembershipPeriod: {
      type: String as PropType<TMembershipPeriodValue>,
      required: true,
    },
  },
  setup() {
    const { createSubscriptionBasket, checkoutContext } = useCheckoutBasket()

    return {
      toPeriod,
      MembershipPeriods,
      Themes,
      createSubscriptionBasket,
      checkoutContext,
      NamedPlanTiers,
    }
  },
  computed: {
    alreadySubscribed(): boolean {
      return this.$userStore.isLoggedOut
        ? false
        : (this.$userStore.user?.subscriptions?.some(
            (p) =>
              this.plan.tier.toLocaleLowerCase() ===
              p.plan_tier.toLocaleLowerCase()
          ) ?? false)
    },
    formattedCost(): string {
      return this.$n(this.plan.cost, 'currency', {
        minimumFractionDigits: 2,
      })
    },
    displayPrice(): number {
      if (this.selectedMembershipPeriod === MembershipPeriods.Yearly) {
        return this.plan.discount_cost
      }

      return this.plan.temp_discount === true
        ? this.plan.discount_cost
        : this.plan.cost
    },
    formattedDisplayPrice(): string {
      return this.$n(this.displayPrice ?? this.plan.cost, 'currency', {
        minimumFractionDigits: 2,
      })
    },
    formattedDiscountCost(): string {
      const value =
        typeof this.plan.discount_cost === 'number'
          ? this.plan.discount_cost
          : this.plan.cost

      return this.$n(value, 'currency', {
        minimumFractionDigits: 2,
      })
    },
    formattedDiscountPercentage(): Nullable<string> {
      if (this.plan.discount_cost && this.plan.cost) {
        const discountPercentage =
          Math.round(
            ((this.plan.cost - this.plan.discount_cost) / this.plan.cost) * 100
          ) / 100

        return discountPercentage === 0
          ? null
          : this.$n(discountPercentage, 'percent')
      }

      return null
    },
    badgeLabel(): string | null {
      if (this.selectedMembershipPeriod === MembershipPeriods.Yearly) {
        if (this.$userStore.hasSubscription) {
          return this.plan.tier === this.$userStore.highestPlanTier
            ? 'Current Plan'
            : null
        }

        // if (this.plan.most_popular) {
        //   return 'Most Popular'
        // }

        if (this.plan.best_value) {
          return 'Best Deal'
        }
      } else if (
        this.plan.show_discount_percentage &&
        this.formattedDiscountPercentage
      ) {
        return `${this.formattedDiscountPercentage} Off First Month`
      }

      return null
    },
    buttonText(): string {
      if (this.$userStore.isLoggedOut) {
        if (this.selectedMembershipPeriod === MembershipPeriods.Monthly) {
          return 'Try 7 Days for Free'
        }
      }

      if (this.alreadySubscribed) {
        return 'Your Current Plan'
      }

      return this.$userStore.highestPlanTier &&
        PlanTiers[this.plan.tier] > PlanTiers[this.$userStore.highestPlanTier]
        ? `Upgrade to ${this.plan.name}`
        : `Get ${this.plan.tier === NamedPlanTiers.Vip ? 'VIP' : this.plan.tier}`
    },
    tierIcon(): TIcon {
      switch (this.plan.tier) {
        case 'standard':
          return this.Icons.StarOutlined
        case 'platinum':
          return this.Icons.LighteningOutlined
        case 'vip':
          return this.Icons.Crown
        default:
          return this.Icons.Star
      }
    },
  },
  mounted() {
    // Pre-populate the basket when component mounts
    this.handlePlanSelection()
  },
  methods: {
    handlePlanSelection() {
      // Create a subscription basket with this plan
      this.createSubscriptionBasket(this.plan)
    },
  },
})
</script>

<template>
  <article
    class="plan-card"
    :class="{
      [plan.theme]: true,
      [plan.tier]: true,
      'bg-gradient-platinum-light': plan.tier === NamedPlanTiers.Platinum,
      'bg-gradient-standard-light': plan.tier === NamedPlanTiers.Standard,
      'bg-gradient-gold-light': plan.tier === NamedPlanTiers.Vip,
    }"
    tabindex="0"
  >
    <div
      class="plan-header"
      :class="{
        'bg-gradient-platinum': plan.tier === NamedPlanTiers.Platinum,
        'bg-gradient-standard': plan.tier === NamedPlanTiers.Standard,
        'bg-gradient-gold': plan.tier === NamedPlanTiers.Vip,
      }"
    >
      <div
        class="flex items-center gap-3 font-bold"
        :class="{
          'text-black':
            plan.tier === NamedPlanTiers.Vip ||
            plan.tier === NamedPlanTiers.Platinum,
        }"
      >
        <AppIcon
          :icon="tierIcon"
          class="hidden @lg/main:inline flex-shrink-0"
        />
        {{ plan.name }}
      </div>

      <AppBadge
        v-if="badgeLabel"
        size="md"
        class="capitalize text-pure-white bg-blue-700 !rounded !px-2 text-xs @sm/main:text-sm"
      >
        {{ badgeLabel }}
      </AppBadge>
    </div>

    <div class="plan-content">
      <div class="flex flex-col gap-2">
        <div class="justify-center items-end flex gap-x-1">
          <div class="text-2xl @md/main:text-2lg/9 font-bold">
            <slot name="cost">
              {{
                formattedDiscountCost !== formattedDisplayPrice
                  ? formattedDiscountCost
                  : formattedDisplayPrice
              }}
            </slot>
          </div>
          <p class="text-base/5 @md/main:text-xl/6 text-[#5F7D92]">
            /{{ toPeriod(selectedMembershipPeriod) }}
          </p>
        </div>

        <p class="text-sm/none text-[#5F7D92] text-center">
          <template v-if="plan.temp_discount">
            *{{ formattedCost }} after first
            {{ toPeriod(selectedMembershipPeriod, 'full') }}
          </template>
          <template v-else> &nbsp; </template>
        </p>
      </div>

      <div
        class="bg-black text-pure-white text-base/5 @sm/main:text-xl/6 font-bold rounded px-4 py-2 text-center w-min mx-auto whitespace-nowrap"
      >
        {{
          $n(
            selectedMembershipPeriod === MembershipPeriods.Yearly
              ? plan.entries * 12
              : plan.entries
          )
        }}
        Entries
        <span class="text-sm/4 @md/main:text-base/5 font-bold"
          >/{{ toPeriod(selectedMembershipPeriod) }}</span
        >
      </div>

      <slot>
        <ProductCheckoutModal
          v-if="$userStore.shouldUseSolidgate"
          :checkout-context="checkoutContext"
        >
          <template #button="{ openModal }">
            <AppButton
              class="w-full font-bold capitalize !h-11 button-primary"
              :title="buttonText"
              :class="{
                'pointer-events-none opacity-50': alreadySubscribed,
              }"
              :icon="alreadySubscribed ? null : Icons.ArrowRight"
              @click="openModal"
            >
              {{ buttonText }}
            </AppButton>
          </template>
        </ProductCheckoutModal>
        <!-- Fallback to old route-based system if feature flag is disabled -->
        <AppButton
          v-if="!$userStore.shouldUseSolidgate"
          :route="{
            name: 'checkout',
            query: { plan: plan.id ?? plan.code },
          }"
          class="w-full font-bold capitalize !h-11 button-primary"
          :title="buttonText"
          :class="{
            'pointer-events-none opacity-50': alreadySubscribed,
          }"
          :icon="alreadySubscribed ? null : Icons.ArrowRight"
        >
          {{ buttonText }}
        </AppButton>
      </slot>

      <ul class="flex flex-col gap-3 leading-5">
        <li
          v-for="(feature, key) in plan.features"
          :key="key"
          class="flex gap-3 items-center"
        >
          <AppIcon
            v-if="feature.pre_icon"
            :icon="feature.pre_icon.icon"
            :class="`${feature.pre_icon.class} icon`"
          />
          <p v-html="feature.title" />
        </li>
      </ul>

      <!-- <ul class="flex flex-col gap-6">
        <li class="flex gap-3">
          <AppIcon :icon="Icons.Ticket" class="text-primary icon" />

          <div class="flex flex-col gap-3">
            <p>
              <span class="font-bold">{{ $n(plan.entries / 30) }} entries</span>
              per draw entered automatically every day.
            </p>
            <p>
              That makes a total of
              <span class="font-bold"
                >{{ $n(plan.entries) }} entries every month</span
              >!
            </p>
          </div>
        </li>

        <li class="flex gap-3">
          <AppIcon :icon="Icons.Present" class="text-primary icon" />

          <div class="flex flex-col gap-3">
            <p v-html="plan.features[2].title" />
          </div>
        </li>
      </ul> -->
    </div>
  </article>
</template>

<style lang="scss" scoped>
.plan-card {
  @apply flex flex-grow flex-col rounded-xl shadow-[0_10px_15px_-4px_#6664] mb-4;

  .plan-header {
    @apply flex px-4 py-3 text-pure-white font-bold text-xl justify-between items-center rounded-t-xl gap-1;
  }

  .plan-content {
    @apply @md/main:p-6 @md/main:pb-10 p-3 pb-6 gap-4 @md/main:gap-6 flex flex-col border-2 border-y-0 border-gray-200 rounded-b-xl flex-grow;
  }

  .icon {
    @apply size-5 @md/main:size-6 flex-shrink-0 !important;
  }

  .asterisk::after {
    @apply content-['*'] font-bold inline-block;
  }
}
</style>
